// Main-thread client for the module-loader worker (see module-loader-worker.ts).
//
// `importAutomergePackageViaWorker` is wired into the ModuleWatcher in place of
// its default (direct, main-thread) package import. It asks the worker to
// import the package entry point and report which plugins it exports, then
// returns the same `{ plugins }` shape the watcher already feeds to
// `registerPlugins` — except each plugin's `load()` re-imports the package
// (pinned to the same heads) on this thread and runs the real plugin loader.
import { importPluginFromFolderDocUrl } from "@inkandswitch/patchwork-filesystem";
const WORKER_PATH = "/module-loader-worker.js";
let worker;
let nextRequestId = 1;
const pending = new Map();
function resolveImportMapValue(value, baseURI) {
    try {
        return new URL(value, baseURI).href;
    }
    catch {
        return value;
    }
}
function resolveImportMap(raw, baseURI) {
    const resolved = {};
    if (raw.imports) {
        resolved.imports = {};
        for (const [key, value] of Object.entries(raw.imports)) {
            resolved.imports[key] = resolveImportMapValue(value, baseURI);
        }
    }
    if (raw.scopes) {
        resolved.scopes = {};
        for (const [scopeKey, scopeMap] of Object.entries(raw.scopes)) {
            const resolvedScopeKey = resolveImportMapValue(scopeKey, baseURI);
            resolved.scopes[resolvedScopeKey] = {};
            for (const [key, value] of Object.entries(scopeMap)) {
                resolved.scopes[resolvedScopeKey][key] = resolveImportMapValue(value, baseURI);
            }
        }
    }
    return resolved;
}
// Multiple import maps apply in document order; later ones fill gaps but don't
// override earlier entries.
function mergeImportMaps(merged, next = {}) {
    if (next.imports) {
        merged.imports ??= {};
        for (const [key, value] of Object.entries(next.imports)) {
            if (!Object.hasOwn(merged.imports, key))
                merged.imports[key] = value;
        }
    }
    if (next.scopes) {
        merged.scopes ??= {};
        for (const [scopeKey, scopeMap] of Object.entries(next.scopes)) {
            const mergedScope = (merged.scopes[scopeKey] ??= {});
            for (const [key, value] of Object.entries(scopeMap)) {
                if (!Object.hasOwn(mergedScope, key))
                    mergedScope[key] = value;
            }
        }
    }
    return merged;
}
function getResolvedImportMap() {
    const merged = {};
    for (const script of document.querySelectorAll('script[type="importmap"]')) {
        if (!script.textContent)
            continue;
        try {
            mergeImportMaps(merged, resolveImportMap(JSON.parse(script.textContent), document.baseURI));
        }
        catch {
            continue;
        }
    }
    return merged;
}
function getWorker() {
    if (worker)
        return worker;
    worker = new Worker(WORKER_PATH, {
        type: "module",
        name: "patchwork-module-loader",
    });
    worker.addEventListener("message", (event) => {
        const data = event.data;
        if (!data ||
            (data.type !== "descriptors" &&
                data.type !== "datatype" &&
                data.type !== "error")) {
            return;
        }
        const entry = pending.get(data.id);
        if (!entry)
            return;
        pending.delete(data.id);
        if (data.type === "descriptors")
            entry.resolve(data.descriptors);
        else if (data.type === "datatype")
            entry.resolve(data.bytes);
        else
            entry.reject(new Error(data.error));
    });
    worker.addEventListener("error", (event) => {
        // An uncaught worker error can't be tied to a single request — fail every
        // outstanding one so callers don't hang.
        const error = new Error(`module-loader worker error: ${event.message ?? "unknown"}`);
        for (const [, entry] of pending)
            entry.reject(error);
        pending.clear();
    });
    return worker;
}
function discoverDescriptors(urlAtHeads) {
    const id = nextRequestId++;
    return new Promise((resolve, reject) => {
        pending.set(id, { resolve, reject });
        getWorker().postMessage({
            type: "discover",
            id,
            url: urlAtHeads,
            importMap: getResolvedImportMap(),
            baseURI: document.baseURI,
        });
    });
}
export function initializeDatatypeViaWorker(importUrl, datatypeId, repoPort) {
    const id = nextRequestId++;
    const worker = new Worker(WORKER_PATH, {
        type: "module",
        name: `patchwork-datatype-${datatypeId}`,
    });
    return new Promise((resolve, reject) => {
        const cleanup = () => {
            worker.terminate();
            pending.delete(id);
        };
        pending.set(id, {
            resolve(value) {
                cleanup();
                resolve(value);
            },
            reject(error) {
                cleanup();
                reject(error);
            },
        });
        worker.addEventListener("message", (event) => {
            const data = event.data;
            if (!data ||
                data.id !== id ||
                (data.type !== "datatype" && data.type !== "error")) {
                return;
            }
            const entry = pending.get(id);
            if (!entry)
                return;
            if (data.type === "datatype")
                entry.resolve(data.bytes);
            else
                entry.reject(new Error(data.error));
        });
        worker.addEventListener("error", (event) => {
            const entry = pending.get(id);
            if (!entry)
                return;
            entry.reject(new Error(`datatype worker error: ${event.message ?? "unknown"}`));
        });
        worker.postMessage({
            type: "init-datatype",
            id,
            importUrl,
            datatypeId,
            importMap: getResolvedImportMap(),
            baseURI: document.baseURI,
        }, [repoPort]);
    });
}
export async function importAutomergePackageViaWorker(urlAtHeads) {
    const url = urlAtHeads;
    const descriptors = await discoverDescriptors(url);
    const plugins = descriptors.map((descriptor) => {
        const { id, type } = descriptor;
        // A plugin id is only unique within a plugin type, so both are needed to
        // re-select the right plugin when its load() re-imports the package.
        if (typeof id !== "string" || typeof type !== "string")
            return descriptor;
        return {
            ...descriptor,
            load: () => importPluginFromFolderDocUrl(url, type, id),
        };
    });
    return { plugins };
}
